import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import ConfirmModal from '../Common/ConfirmModal';
import { useUnsavedChangesWarning } from '../../hooks/useUnsavedChangesWarning';
import { allowDraftLeave } from '../../utils/draftLeaveGuard';
import { storyBibleApi } from '../../api/storyBible';
import { pendingOffersApi } from '../../api/pendingOffers';
import { bindNoteRefresh } from '../../utils/noteChanged';
import DatedNoteLines from './DatedNoteLines';
import NoteRedraftConfirm from './NoteRedraftConfirm';
import { usePaidConfirm } from '../Common/usePaidConfirm';
import { sessionsApi } from '../../api/sessions';
import { entitiesApi } from '../../api/entities';
import { dossiersApi } from '../../api/dossiers';
import { plotDossiersApi } from '../../api/plotDossiers';
import { isSingletonCategory, focusTemplateForCategory, categoryDisplayName } from './categories';
import {
  OPEN_WRITEUP,
  PLOT_NOTEBOOK_PATH,
  characterWriteupHasContent,
  normCardName,
  plotWriteupHasContent,
} from '../../utils/bookCards';
import {
  formatPersonLabel,
  parsePersonLabel,
  personFocusNames,
} from '../../utils/personNames';
import CharacterDossier from '../Entities/CharacterDossier';
import {
  BAND_FROM_SERIES,
  BAND_THIS_BOOK,
  EMPTY_THIS_BOOK,
} from '../../pages/bringIntoBookCopy';
import {
  APPROVE_NEEDS_TEXT,
  CUSTOM_NOTE,
  CUSTOM_NOTE_NEEDS_TITLE,
  DELETE_NOTE,
  DELETE_NOTE_CONFIRM,
  DISCARD_UNSAVED,
  KEEP_EDITING,
  CHANGE_LOG,
  CHANGE_LOG_EMPTY,
  CHANGE_LOG_ERROR,
  HIDE_CHANGE_LOG,
  IDEA_OFFER_DISMISS_FAILED,
  IDEA_OFFER_SAVE_FAILED,
  NOTE_NEEDS_DESCRIPTION,
  REDRAFT,
  REDRAFT_ACCEPT,
  REDRAFT_ACCEPTED,
  REDRAFT_FAILED,
  REDRAFT_HOVER,
  REDRAFT_NOT_STORED,
  REDRAFT_NOTHING_TO_DRAW_FROM,
  REDRAFT_NOTE_CHANGED,
  REDRAFT_OFFER_HEADING,
  REDRAFT_OFFER_LINE,
  REDRAFT_REJECT,
  REDRAFT_REJECTED,
  REDRAFTING,
  NOTE_TITLE,
  UNSAVED_CHANGES_WARNING,
  UNSAVED_CLOSE_CHOICE,
} from '../../utils/writerCopy';
import { savedOnLabel } from '../../utils/noteHistory';
import './BookSheet.css';
import '../../pages/BringIntoBookPage.css';

/**
 * CardDetailPanel — Book Sheet Cards Part 3/4/5's single interaction
 * surface. Mirrors SeriesSheet.jsx's master-detail panel pattern (portal to
 * document.body, pinned to the window's right edge) rather than inventing a
 * new visual language, minus the resize handle (not required by this
 * increment's spec, and every field here is a single description block —
 * a fixed width reads fine).
 *
 * `card` is either:
 *  - an existing, already-persisted StoryBibleEntry (has `.id`) — Approve
 *    here means PATCH (Part 5: "Approve calls add-cards or PATCH depending
 *    on new-vs-existing").
 *  - a draft descriptor {category, tier, instanceName, unnamedFlagged,
 *    isDraft: true} with no id yet — nothing exists server-side; Approve
 *    calls add-cards, which persists AND confirms in one step (Part 4's
 *    doc comment: approving IS confirming for Book Sheet cards).
 */
const OUTSIDE_NOTE_CLICK = [
  '.bs-detail',
  '.bs-modal',
  '.bs-modal-backdrop',
  '.cm-backdrop',
  '.paid-confirm',
  '.paid-confirm-backdrop',
  '.brainstormy-sidebar',
  '.mobile-tab-bar',
  '.drawer-panel',
  '.drawer-backdrop',
  '.mobile-header',
].join(', ');

const PLOT_THEME_REQUIRES_THEME_MESSAGE = (
  'Logline requires a Theme note first — the logline is only meaningful as the concrete expression of an underlying theme. No Theme note exists yet for this project.\n\n'
  + 'Clicking Generate will create both: a Theme note first, then the Logline note built from it.'
);

export default function CardDetailPanel({ storyId, card, onClose, onSaved, onDiscardDraft, characterNames = [], themePreview = '', hasTheme = false, wantWriteup = false, seriesBand = '', seriesCiteName = '', focusOnOpen = false }) {
  const navigate = useNavigate();
  const isExisting = !!card?.id;
  const category = card.category;
  const tier = card.tier;
  const isCustom = (tier || '').toLowerCase() === 'custom';
  const instanceName = isExisting ? card.instance_name : card.instanceName;
  const singleton = isSingletonCategory(category);
  const seedLabel = isExisting ? (card.name || instanceName || '') : (instanceName || (singleton ? category : ''));
  const seedParsed = parsePersonLabel(seedLabel);
  const seedNickname = card.nickname || seedParsed.nickname || '';

  const [description, setDescription] = useState(card.description || '');
  const [name, setName] = useState(seedParsed.name || seedLabel);
  const [nickname, setNickname] = useState(seedNickname);
  const isCharacter = (category || '').toLowerCase() === 'character';
  const alsoKnownAs = personFocusNames({
    name: seedLabel,
    nickname: seedNickname,
    alsoKnownAs: card.alsoKnownAs || card.also_known_as || [],
  });
  const [descriptionStyle, setDescriptionStyle] = useState('simple');
  const [lastGenerated, setLastGenerated] = useState('');
  const [generating, setGenerating] = useState(false);
  const [redrafting, setRedrafting] = useState(false);
  const redraftFlight = useRef(false);
  const [confirmRedraft, setConfirmRedraft] = useState(false);
  const [redraftOffer, setRedraftOffer] = useState(null);
  const [redraftMessage, setRedraftMessage] = useState('');
  const [redraftBusy, setRedraftBusy] = useState(false);
  const [acceptedText, setAcceptedText] = useState(null);
  const confirmRedraftRef = useRef(false);
  confirmRedraftRef.current = confirmRedraft;
  const { ask: askPaid, dialog: paidDialog } = usePaidConfirm();
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState(null);
  const [logOpen, setLogOpen] = useState(false);
  const [earlierSaves, setEarlierSaves] = useState(null);
  const [logError, setLogError] = useState(null);
  const [logLoading, setLogLoading] = useState(false);
  const [showThemeAlert, setShowThemeAlert] = useState(false);
  const [writeupEntity, setWriteupEntity] = useState(null);
  const [hasWriteup, setHasWriteup] = useState(false);
  const [showWriteup, setShowWriteup] = useState(false);
  const descriptionRef = useRef(null);
  const isPlot = (category || '').trim() === 'Plot';
  const canHoldWriteup = isExisting && (isCharacter || isPlot);
  // An existing World note may be cleared. A blank description is a real
  // save, not a missing one. New notes, and every other category, still
  // need text before Approve / Save changes.
  const canClearDescription = isExisting && (category || '').trim() === 'World';

  // Increment 2, Relationship "Parties" nice-to-have (spec: "autocomplete
  // against confirmed Character notes ... if it fits without adding real
  // scope"). Part 1's card primitive is deliberately just one description
  // field — there's no separate structured "Parties" input to autocomplete
  // against, and Part 9's Parties/Starting-dynamic/Arc are prose
  // sub-answers within that one field, not separate ones. A real typeahead
  // (a mentions-style dropdown inside a plain textarea) would be a genuine
  // new interaction surface, not "reuse existing data, no new work" — so
  // this is a click-to-insert reference row instead: cheap (no new
  // endpoint, no new component, reuses names already loaded for the grid),
  // and still serves the actual need (canonical name spelling) without
  // inventing autocomplete-in-a-textarea.
  const isRelationship = (category || '').toLowerCase() === 'relationship';
  const insertCharacterName = (charName) => {
    const el = descriptionRef.current;
    if (!el) {
      setDescription((d) => (d ? `${d} ${charName}` : charName));
      return;
    }
    const start = el.selectionStart ?? description.length;
    const end = el.selectionEnd ?? description.length;
    const next = description.slice(0, start) + charName + description.slice(end);
    setDescription(next);
    requestAnimationFrame(() => {
      el.focus();
      const caret = start + charName.length;
      el.setSelectionRange(caret, caret);
    });
  };

  // A different card was opened — reset local editing state rather than
  // carrying over the previous card's in-progress text.
  useEffect(() => {
    const nextLabel = isExisting ? (card.name || instanceName || '') : (instanceName || (singleton ? category : ''));
    const nextParsed = parsePersonLabel(nextLabel);
    setDescription(card.description || '');
    setName(nextParsed.name || nextLabel);
    setNickname(card.nickname || nextParsed.nickname || '');
    setDescriptionStyle('simple');
    setLastGenerated('');
    setError(null);
    setShowThemeAlert(false);
    setWriteupEntity(null);
    setHasWriteup(false);
    setShowWriteup(false);
    setAcceptedText(null);
    setRedraftMessage('');
    setConfirmRedraft(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [card.id, card.category, card.instanceName, card.nickname, card.tier]);

  const offerLoad = useRef(0);
  useEffect(() => {
    if (!storyId || !isExisting) {
      setRedraftOffer(null);
      return undefined;
    }
    let cancelled = false;
    const load = async () => {
      const token = ++offerLoad.current;
      try {
        const data = await pendingOffersApi.list(storyId);
        if (cancelled || token !== offerLoad.current) return;
        const match = (data?.offers || []).find((offer) => (
          offer.kind === 'redraft' && String(offer.note_id) === String(card.id)
        ));
        setRedraftOffer(match || null);
      } catch {
        if (!cancelled && token === offerLoad.current) setRedraftOffer(null);
      }
    };
    load();
    const unbind = bindNoteRefresh(storyId, () => { void load(); });
    return () => {
      cancelled = true;
      unbind();
    };
  }, [storyId, isExisting, card.id]);

  useEffect(() => {
    if (!focusOnOpen) return;
    const el = descriptionRef.current;
    if (!el) return;
    el.focus();
    const len = el.value.length;
    try {
      el.setSelectionRange(len, len);
    } catch {
      // some inputs reject selection
    }
  }, [focusOnOpen, card.id, card.category]);

  useEffect(() => {
    if (!canHoldWriteup || !storyId) return undefined;
    let cancelled = false;
    const probe = async () => {
      try {
        if (isPlot) {
          const view = await plotDossiersApi.get(storyId);
          if (cancelled) return;
          const ready = plotWriteupHasContent(view);
          setHasWriteup(ready);
          setShowWriteup(false);
          if (wantWriteup && ready && allowDraftLeave()) navigate(PLOT_NOTEBOOK_PATH);
          return;
        }
        const wanted = normCardName(parsePersonLabel(instanceName || card.name).name || instanceName || card.name);
        if (!wanted) {
          setHasWriteup(false);
          return;
        }
        const res = await entitiesApi.listByStory(storyId, 'character');
        const list = Array.isArray(res) ? res : (res?.data || res?.anchors || []);
        const matches = list.filter((entity) => (
          normCardName(entity.canonical_name || entity.name) === wanted
        ));
        if (cancelled) return;
        if (matches.length !== 1) {
          setWriteupEntity(null);
          setHasWriteup(false);
          setShowWriteup(false);
          return;
        }
        const entity = matches[0];
        const view = await dossiersApi.get(entity.id);
        if (cancelled) return;
        const ready = characterWriteupHasContent(view);
        setWriteupEntity(ready ? entity : null);
        setHasWriteup(ready);
        setShowWriteup(Boolean(wantWriteup && ready));
      } catch {
        if (!cancelled) {
          setHasWriteup(false);
          setShowWriteup(false);
        }
      }
    };
    probe();
    return () => { cancelled = true; };
  }, [canHoldWriteup, storyId, isPlot, instanceName, card.name, wantWriteup, navigate]);

  const showNameFields = isCharacter || isCustom || (isExisting && card.unnamed_flagged);
  const savedLabel = isExisting ? savedOnLabel(card.updated_at || card.created_at) : '';

  useEffect(() => {
    setLogOpen(false);
    setEarlierSaves(null);
    setLogError(null);
    setLogLoading(false);
  }, [card?.id]);

  const toggleChangeLog = async () => {
    if (logOpen) {
      setLogOpen(false);
      return;
    }
    setLogOpen(true);
    setLogError(null);
    setLogLoading(true);
    try {
      const data = await storyBibleApi.listHistory(storyId, card.id);
      setEarlierSaves(Array.isArray(data?.earlier) ? data.earlier : []);
    } catch (e) {
      setEarlierSaves([]);
      setLogError(e.message || CHANGE_LOG_ERROR);
    } finally {
      setLogLoading(false);
    }
  };

  const savedDescription = acceptedText != null ? acceptedText : (card.description || '');
  const savedName = seedParsed.name || seedLabel;
  const savedNickname = seedNickname;
  const dirty = description !== savedDescription
    || (showNameFields && name !== savedName)
    || (isCharacter && nickname !== savedNickname);
  const dirtyRef = useRef(false);
  dirtyRef.current = dirty;
  const askOpenRef = useRef(false);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const [askOpen, setAskOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const deleteOpenRef = useRef(false);
  useUnsavedChangesWarning(dirty);

  const requestClose = () => {
    if (askOpenRef.current) return;
    if (!dirtyRef.current) {
      onCloseRef.current?.();
      return;
    }
    askOpenRef.current = true;
    setAskOpen(true);
  };
  const requestCloseRef = useRef(requestClose);
  requestCloseRef.current = requestClose;

  const keepEditing = () => {
    askOpenRef.current = false;
    setAskOpen(false);
  };

  const discardEdits = () => {
    askOpenRef.current = false;
    setAskOpen(false);
    onCloseRef.current?.();
  };

  useEffect(() => {
    if (!card) return undefined;
    const onKey = (e) => {
      if (e.key !== 'Escape' || askOpenRef.current || deleteOpenRef.current || confirmRedraftRef.current) return;
      requestCloseRef.current();
    };
    const onOutside = (event) => {
      if (!dirtyRef.current || askOpenRef.current || deleteOpenRef.current) return;
      const target = event.target;
      if (!(target instanceof Element) || target.closest(OUTSIDE_NOTE_CLICK)) return;
      event.preventDefault();
      event.stopPropagation();
      requestCloseRef.current();
    };
    window.addEventListener('keydown', onKey);
    document.addEventListener('click', onOutside, true);
    return () => {
      window.removeEventListener('keydown', onKey);
      document.removeEventListener('click', onOutside, true);
    };
  }, [card]);

  const panelPrimary = (name || '').trim();
  const panelNickname = (nickname || '').trim();
  const displayTitle = isCharacter
    ? (formatPersonLabel(panelPrimary || instanceName, panelNickname) || category || CUSTOM_NOTE)
    : (instanceName || category || CUSTOM_NOTE);
  const generateInstanceName = showNameFields
    ? (panelPrimary || instanceName || null)
    : (instanceName || null);
  const generateAlsoKnownAs = isCharacter
    ? personFocusNames({
      name: panelPrimary || instanceName || '',
      nickname: panelNickname,
      alsoKnownAs,
    })
    : [];

  const applyGenerateResult = async (result) => {
    const next = result.description || '';
    setDescription(next);
    setLastGenerated(next.trim());
    if (!isExisting && !instanceName && result.name) setName(result.name);
    if (result.theme_newly_generated) await onSaved?.();
  };

  const isNeedsThemeError = (e) => {
    if (e?.status !== 409) return false;
    const detail = e.message;
    if (detail && typeof detail === 'object' && detail.code === 'needs_theme') return true;
    return String(detail || '').includes('Theme note first');
  };

  const runGenerate = async ({ generateBoth = false } = {}) => {
    const ok = await askPaid(generateBoth ? 'bible_both' : 'bible_card');
    if (!ok) return;
    setGenerating(true);
    setError(null);
    try {
      const trimmed = (description || '').trim();
      const result = await storyBibleApi.generateCard(storyId, {
        category,
        instanceName: generateInstanceName,
        // Part 3: the description field doubles as the seed prompt — a
        // writer can paste notes before generating. Do NOT resend the last
        // generated draft as seed: a mid-sentence cutoff then becomes
        // "the writer trailed off" and the next pass dies the same way.
        seedText: (trimmed && trimmed !== lastGenerated) ? trimmed : null,
        descriptionStyle: isCharacter ? descriptionStyle : null,
        generateBoth,
        nickname: isCharacter ? (panelNickname || null) : null,
        alsoKnownAs: generateAlsoKnownAs.length ? generateAlsoKnownAs : null,
      });
      await applyGenerateResult(result);
    } catch (e) {
      if (isPlot && isNeedsThemeError(e)) {
        setShowThemeAlert(true);
        return;
      }
      setError(e.message || 'Generation failed.');
    } finally {
      setGenerating(false);
    }
  };

  const acceptRedraft = async () => {
    if (!redraftOffer || redraftBusy) return;
    setRedraftBusy(true);
    setRedraftMessage('');
    try {
      await pendingOffersApi.accept(storyId, {
        kind: 'redraft',
        id: redraftOffer.id,
        source_ref: redraftOffer.note_id || redraftOffer.source_ref || null,
      });
      const next = redraftOffer.detail || '';
      setDescription(next);
      setAcceptedText(next);
      setRedraftOffer(null);
      setRedraftMessage(REDRAFT_ACCEPTED);
      await onSaved?.();
    } catch (err) {
      setRedraftMessage(err?.status === 409 ? REDRAFT_NOTE_CHANGED : IDEA_OFFER_SAVE_FAILED);
    } finally {
      setRedraftBusy(false);
    }
  };

  const rejectRedraft = async () => {
    if (!redraftOffer || redraftBusy) return;
    setRedraftBusy(true);
    setRedraftMessage('');
    try {
      await pendingOffersApi.reject(storyId, {
        kind: 'redraft',
        id: redraftOffer.id,
        source_ref: redraftOffer.note_id || redraftOffer.source_ref || null,
      });
      setRedraftOffer(null);
      setRedraftMessage(REDRAFT_REJECTED);
    } catch {
      setRedraftMessage(IDEA_OFFER_DISMISS_FAILED);
    } finally {
      setRedraftBusy(false);
    }
  };

  const runRedraft = async () => {
    if (redraftFlight.current) return;
    redraftFlight.current = true;
    setConfirmRedraft(false);
    setRedrafting(true);
    setRedraftMessage('');
    try {
      const result = await storyBibleApi.redraft(storyId, card.id);
      offerLoad.current += 1;
      if (result?.nothing_to_draw_from) {
        setRedraftOffer(null);
        setRedraftMessage(REDRAFT_NOTHING_TO_DRAW_FROM);
        return;
      }
      setRedraftOffer(result?.offer || null);
    } catch (err) {
      setRedraftMessage(err?.message === REDRAFT_NOT_STORED ? REDRAFT_NOT_STORED : REDRAFT_FAILED);
    } finally {
      redraftFlight.current = false;
      setRedrafting(false);
    }
  };

  const handleGenerate = async () => {
    // Step 0 — halt before any generation when no Theme card exists
    // (any persisted review state counts as YES; empty client drafts do not).
    if (isPlot && !hasTheme) {
      setShowThemeAlert(true);
      return;
    }
    await runGenerate({ generateBoth: false });
  };

  const handleGenerateBoth = async () => {
    setShowThemeAlert(false);
    await runGenerate({ generateBoth: true });
  };

  const handleCancelThemeAlert = () => {
    if (generating) return;
    setShowThemeAlert(false);
  };

  const handleApprove = async () => {
    const trimmedDesc = description.trim();
    if (!trimmedDesc && !canClearDescription) {
      setError(NOTE_NEEDS_DESCRIPTION);
      return;
    }
    // Custom cards have no category-derived name to fall back on (unlike
    // Tier 1/2 singletons, whose name is the category, or multi-instance
    // cards, whose name comes from the wizard/add form) — a blank Title
    // here would silently persist an unnamed, unflagged card with no way
    // to find it again. Everything else (Tier 1/2) always has a name by
    // construction, so this check only applies to new custom cards.
    if (!isExisting && isCustom && !(name || '').trim()) {
      setError(CUSTOM_NOTE_NEEDS_TITLE);
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const persistedLabel = isCharacter
        ? formatPersonLabel(panelPrimary || instanceName, panelNickname)
        : (showNameFields ? (panelPrimary || instanceName || null) : (instanceName || (singleton ? category : null)));
      if (isExisting) {
        await storyBibleApi.updateEntry(storyId, card.id, {
          name: persistedLabel || null,
          description: trimmedDesc,
          instance_name: isCharacter ? (persistedLabel || null) : undefined,
        });
      } else {
        const cardName = isCustom ? (panelPrimary || persistedLabel || null) : (persistedLabel || (singleton ? category : null));
        await storyBibleApi.addCards(storyId, [{
          name: cardName,
          description: trimmedDesc,
          category,
          tier,
          instance_name: persistedLabel || instanceName || null,
          unnamed_flagged: isCharacter ? !panelPrimary : !!card.unnamedFlagged,
        }]);
        onDiscardDraft?.();
      }
      await onSaved?.();
      onClose();
    } catch (e) {
      setError(e.message || 'Save failed.');
    } finally {
      setSaving(false);
    }
  };

  const handleDiscard = () => {
    if (!isExisting) {
      onDiscardDraft?.();
      onClose();
      return;
    }
    if (!dirtyRef.current) return;
    setDescription(savedDescription);
    setName(savedName);
    setNickname(savedNickname);
    setError(null);
  };

  const askDelete = () => {
    if (!isExisting || deleting) return;
    deleteOpenRef.current = true;
    setDeleteOpen(true);
  };

  const cancelDelete = () => {
    deleteOpenRef.current = false;
    setDeleteOpen(false);
  };

  const confirmDelete = async () => {
    if (!isExisting) return;
    deleteOpenRef.current = false;
    setDeleteOpen(false);
    setDeleting(true);
    setError(null);
    try {
      await storyBibleApi.deleteEntry(storyId, card.id);
      await onSaved?.();
      onClose();
    } catch (e) {
      setError(e.message || 'Could not delete this note.');
      setDeleting(false);
    }
  };

  const handleOpenFocusSession = async () => {
    setError(null);
    try {
      const template = focusTemplateForCategory(category);
      const response = await sessionsApi.create(storyId, {
        guidance_mode: 'focus',
        template,
        focus_target_name: instanceName || category || null,
      });
      const session = response?.data || response;
      if (session?.id) {
        if (!allowDraftLeave()) return;
        navigate(`/chat/${session.id}`);
      }
    } catch (e) {
      // 'workshop_theme' (Theme cards) enforces one-per-story uniqueness
      // server-side (409) — if a theme workshop session already exists,
      // open that one instead of just surfacing the conflict as an error.
      if (e.status === 409) {
        try {
          const template = focusTemplateForCategory(category);
          const response = await sessionsApi.list(storyId);
          const sessions = response?.data || response || [];
          const existing = sessions.find((s) => s.guidance_mode === 'focus' && s.template === template);
          if (existing?.id) {
            if (!allowDraftLeave()) return;
            navigate(`/chat/${existing.id}`);
            return;
          }
        } catch (_lookupErr) {
          // fall through to the generic error below
        }
      }
      setError(e.message || 'Could not open a Focus session.');
    }
  };

  return createPortal((
    <>
    {paidDialog}
    {confirmRedraft && (
      <NoteRedraftConfirm
        onCancel={() => setConfirmRedraft(false)}
        onConfirm={() => { void runRedraft(); }}
      />
    )}
    <div className={`bs-detail${showWriteup ? ' bs-detail--writeup' : ''}`} role="region" aria-label={`${displayTitle} details`}>
      <div className="bs-detail__header">
        <div>
          <span className="bs-detail__eyebrow">{categoryDisplayName(category)}{isExisting && !card.confirmed ? ' · proposed' : ''}</span>
          <h2 className="bs-detail__title">{(category || '').trim() === 'Plot' ? 'Logline' : displayTitle}</h2>
        </div>
        <button className="bs-detail__close" onClick={requestClose} aria-label="Close">&times;</button>
      </div>

      {isExisting && (
        <div className="bs-change-log">
          {savedLabel ? <p className="bs-saved-on">{savedLabel}</p> : null}
          <button
            type="button"
            className="bs-change-log__toggle"
            aria-expanded={logOpen}
            onClick={toggleChangeLog}
          >
            {logOpen ? HIDE_CHANGE_LOG : CHANGE_LOG}
          </button>
          {logOpen && (
            <div className="bs-change-log__panel">
              {logLoading && <p className="bs-saved-on">Opening the change log…</p>}
              {logError && <p className="bs-error">{logError}</p>}
              {!logLoading && !logError && earlierSaves && earlierSaves.length === 0 && (
                <p className="bs-saved-on">{CHANGE_LOG_EMPTY}</p>
              )}
              {!logLoading && earlierSaves && earlierSaves.length > 0 && (
                <ul className="bs-change-log__list">
                  {earlierSaves.map((item) => (
                    <li key={item.id} className="bs-change-log__item">
                      <p className="bs-saved-on">{savedOnLabel(item.saved_at)}</p>
                      {item.name ? <p className="bs-change-log__name">{item.name}</p> : null}
                      {item.content ? <p className="bs-change-log__body">{item.content}</p> : null}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>
      )}

      {error && <div className="bs-error">{error}</div>}

      {isCharacter && (
        <div className="bs-name-fields">
          <div className="bs-edit__field">
            <label className="bs-edit__label" htmlFor="bs-card-name">Name</label>
            <input
              id="bs-card-name"
              className="bs-edit__input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Primary name — add a surname if you want"
            />
          </div>
          <div className="bs-edit__field">
            <label className="bs-edit__label" htmlFor="bs-card-nickname">Nickname</label>
            <input
              id="bs-card-nickname"
              className="bs-edit__input"
              value={nickname}
              onChange={(e) => setNickname(e.target.value)}
              placeholder="Optional — only if chat linked it, or you type it"
            />
          </div>
        </div>
      )}

      {!isCharacter && (isCustom || (isExisting && card.unnamed_flagged)) && (
        <div className="bs-edit__field">
          <label className="bs-edit__label" htmlFor="bs-card-name">{isCustom ? 'Title' : 'Name'}</label>
          <input
            id="bs-card-name"
            className="bs-edit__input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={isExisting && card.unnamed_flagged ? "No name yet — leave blank to keep unnamed" : NOTE_TITLE}
          />
        </div>
      )}

      {(category || '').trim() === 'Plot' && themePreview && (
        <div className="bs-edit__field">
          <label className="bs-edit__label">Theme (read-only)</label>
          <p className="bs-theme-preview">{themePreview}</p>
        </div>
      )}

      {seriesBand ? (
        <div className="bs-series-band">
          <p className="bs-series-band__label">{BAND_FROM_SERIES}</p>
          {seriesCiteName ? (
            <p className="bs-series-band__cite">{seriesCiteName}</p>
          ) : null}
          <p className="bs-series-band__body">{seriesBand}</p>
        </div>
      ) : null}

      <div className="bs-edit__field">
        <label className="bs-edit__label">{seriesBand ? BAND_THIS_BOOK : 'Description'}</label>
        {isRelationship && characterNames.length > 0 && (
          <div className="bs-char-chips" role="group" aria-label="Insert a character name">
            <span className="bs-char-chips__label">Characters:</span>
            {characterNames.map((charName) => (
              <button
                key={charName}
                type="button"
                className="bs-char-chip"
                onClick={() => insertCharacterName(charName)}
              >
                {charName}
              </button>
            ))}
          </div>
        )}
        <textarea
          ref={descriptionRef}
          className="bs-edit__textarea"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          rows={10}
          autoFocus={focusOnOpen}
          placeholder={seriesBand ? EMPTY_THIS_BOOK : 'Generate from your sessions, or paste notes / write this yourself.'}
        />
      </div>

      {redraftMessage ? <p className="bs-saved-on" role="status">{redraftMessage}</p> : null}

      {redraftOffer ? (
        <section className="bs-edit__field" data-testid="note-redraft-offer" aria-label={REDRAFT_OFFER_HEADING}>
          <h3 className="bs-edit__label">{REDRAFT_OFFER_HEADING}</h3>
          <p className="bs-saved-on">{REDRAFT_OFFER_LINE}</p>
          <p className="bs-change-log__body">{redraftOffer.detail}</p>
          <div className="bs-detail__actions">
            <button
              type="button"
              className="bs-btn bs-btn--secondary"
              disabled={redraftBusy}
              onClick={() => { void acceptRedraft(); }}
            >
              {REDRAFT_ACCEPT}
            </button>
            <button
              type="button"
              className="bs-btn bs-btn--secondary"
              disabled={redraftBusy}
              onClick={() => { void rejectRedraft(); }}
            >
              {REDRAFT_REJECT}
            </button>
          </div>
        </section>
      ) : null}

      {isExisting && (
        <DatedNoteLines storyId={storyId} noteId={card.id} />
      )}

      {isCharacter && (
        <fieldset className="bs-style-choice">
          <legend className="bs-edit__label">When generating</legend>
          <div className="bs-view-toggle" role="radiogroup" aria-label="Character description kind">
            <button
              type="button"
              role="radio"
              aria-checked={descriptionStyle === 'simple'}
              className={`bs-view-toggle__btn ${descriptionStyle === 'simple' ? 'bs-view-toggle__btn--active' : ''}`}
              onClick={() => setDescriptionStyle('simple')}
            >
              Simple portrait
            </button>
            <button
              type="button"
              role="radio"
              aria-checked={descriptionStyle === 'arc'}
              className={`bs-view-toggle__btn ${descriptionStyle === 'arc' ? 'bs-view-toggle__btn--active' : ''}`}
              onClick={() => setDescriptionStyle('arc')}
            >
              With an arc
            </button>
          </div>
          <p className="bs-style-choice__hint">
            {descriptionStyle === 'arc'
              ? 'Want, obstacle, and whether they change. Use this for characters who have a journey.'
              : 'Who they are and what you already established. No invented journey.'}
          </p>
        </fieldset>
      )}

      <div className="bs-detail__actions">
        <button className="bs-btn bs-btn--secondary" onClick={handleGenerate} disabled={generating || saving || redrafting}>
          {generating ? 'Generating…' : (description.trim() ? 'Regenerate' : 'Generate')}
        </button>
        {isExisting && (
          <button
            type="button"
            className="bs-btn bs-btn--secondary"
            title={REDRAFT_HOVER}
            onClick={() => {
              if (redraftFlight.current || redrafting) return;
              setConfirmRedraft(true);
            }}
            disabled={redrafting || saving || generating}
          >
            {redrafting ? REDRAFTING : REDRAFT}
          </button>
        )}
        <button className="bs-btn bs-btn--primary" onClick={handleApprove} disabled={saving || generating || (!description.trim() && !canClearDescription) || (!isExisting && isCustom && !(name || '').trim())}>
          {saving ? 'Saving…' : (isExisting ? 'Save changes' : 'Approve')}
        </button>
        <button
          type="button"
          className="bs-btn bs-btn--danger"
          onClick={handleDiscard}
          disabled={deleting || (isExisting && !dirty)}
        >
          Discard
        </button>
        {isExisting && (
          <button
            type="button"
            className="bs-btn bs-btn--danger"
            onClick={askDelete}
            disabled={deleting || saving || generating}
          >
            {deleting ? 'Deleting…' : DELETE_NOTE}
          </button>
        )}
        {!isExisting && !description.trim() && (
          <p className="bs-approve-hint">{APPROVE_NEEDS_TEXT}</p>
        )}
      </div>

      {hasWriteup && isPlot && (
        <button type="button" className="bs-add-link" onClick={() => { if (allowDraftLeave()) navigate(PLOT_NOTEBOOK_PATH); }}>
          {OPEN_WRITEUP}
        </button>
      )}

      {hasWriteup && !isPlot && !showWriteup && (
        <button type="button" className="bs-add-link" onClick={() => setShowWriteup(true)}>
          {OPEN_WRITEUP}
        </button>
      )}

      {showWriteup && hasWriteup && isCharacter && writeupEntity?.id && (
        <div className="bs-writeup" aria-label="Long write-up">
          <CharacterDossier
            entityId={writeupEntity.id}
            entityName={writeupEntity.canonical_name || writeupEntity.name || displayTitle}
            variant="page"
          />
        </div>
      )}

      <div className="bs-detail__escape-hatch">
        <button className="bs-add-link" onClick={handleOpenFocusSession}>
          Not enough material? Open a Focus session →
        </button>
      </div>
    </div>
    <ConfirmModal
      isOpen={askOpen}
      title={UNSAVED_CHANGES_WARNING}
      message={UNSAVED_CLOSE_CHOICE}
      confirmLabel={DISCARD_UNSAVED}
      cancelLabel={KEEP_EDITING}
      destructive
      onConfirm={discardEdits}
      onCancel={keepEditing}
    />
    <ConfirmModal
      isOpen={deleteOpen}
      title={DELETE_NOTE_CONFIRM}
      confirmLabel={DELETE_NOTE}
      cancelLabel="Cancel"
      destructive
      onConfirm={confirmDelete}
      onCancel={cancelDelete}
    />
    {showThemeAlert && (
      <div className="bs-modal-backdrop" onClick={handleCancelThemeAlert}>
        <div
          className="bs-modal"
          role="dialog"
          aria-modal="true"
          aria-labelledby="bs-theme-required-title"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="bs-modal__header">
            <h2 id="bs-theme-required-title">Theme note required</h2>
            <button className="bs-modal__close" onClick={handleCancelThemeAlert} disabled={generating} aria-label="Close">&times;</button>
          </div>
          <p className="bs-modal__intro bs-modal__intro--pre">{PLOT_THEME_REQUIRES_THEME_MESSAGE}</p>
          <div className="bs-modal__actions">
            <button type="button" className="bs-btn bs-btn--primary" onClick={handleGenerateBoth} disabled={generating}>
              {generating ? 'Generating…' : 'Generate Both'}
            </button>
            <button type="button" className="bs-btn bs-btn--secondary" onClick={handleCancelThemeAlert} disabled={generating}>
              Cancel
            </button>
          </div>
        </div>
      </div>
    )}
    </>
  ), document.body);
}
